import * as waitUntil from 'async-wait-until';
import { buildUrl } from './BuildUrl';
import {
    HeaderType,
    HeaderObject,
    root,
    IRequestTransformer,
    IRequestTransformerConfig
} from '@estee/elc-universal-utils';
import * as fetchJsonp from 'fetch-jsonp';

const BYPASS_CACHE_HEADER_KEY = 'Cache-control';
const BYPASS_CACHE_HEADER_VALUE = 'no-cache, no-store, must-revalidate';

export interface IError {
    message: string;
    status: number;
}

export class ApiError extends Error implements IError {
    public status: number;
    public errorResponse: object;

    constructor(httpStatus: number, errorResponse: object) {
        super(`${httpStatus}`);
        this.status = httpStatus;
        this.errorResponse = errorResponse;
    }
}

export interface IPayload {
    url: string;
    body?: {};
    options?: {};
    headers?: {};
    returnAsPlainText?: boolean;
}

export interface IResponse<T> {
    data: T | null;
    reponseHeaders: Headers;
}

export interface IGetPayload {
    url: string;
    options?: {};
    headers?: {
        [key: string]: {};
    };
    params?: {};
}

export interface IGetPayloads {
    [key: string]: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        promise: Promise<any>;
        timestamp: number;
    };
}

export interface IRequestOptions {
    debounceInterval?: number;
    bypassCache?: boolean;
}

export type IHeaderSetter = () => Promise<HeaderObject>;

export class HttpTransport {
    private baseUrl: string;
    private getRequestPayloads: IGetPayloads = {};
    private globalHeaders: HeaderObject = {};

    private globalHeaderSetters: IHeaderSetter[] = [];

    private requestTransformers: IRequestTransformer[] = [];

    private _autoHandleFail: boolean = true;

    public get isAuthFailureHandlingEnabled() {
        return this._autoHandleFail;
    }

    public setAuthFailureHandlingEnabled(isAuthHandlingEnabled: boolean) {
        this._autoHandleFail = isAuthHandlingEnabled;
    }

    public setBaseUrl = (baseUrl: string) => {
        if (!this.baseUrl) {
            this.baseUrl = baseUrl;
        }
    };

    public getStaticGlobalHeaders() {
        return { ...this.globalHeaders };
    }

    public addGlobalHeaders = (headers: HeaderObject) => {
        this.globalHeaders = { ...this.globalHeaders, ...headers };
    };

    public removeGlobalHeader = (headerName: HeaderType) => {
        delete this.globalHeaders[headerName];
    };

    public addGlobalHeaderSetter = (setter: IHeaderSetter) => {
        if (!this.globalHeaderSetters.includes(setter)) {
            this.globalHeaderSetters.push(setter);
        }
    };

    public removeGlobalHeaderSetter = (setter: IHeaderSetter) => {
        this.globalHeaderSetters = this.globalHeaderSetters.filter(
            headerSetter => headerSetter !== setter
        );
    };

    public addRequestTransformer = (transformer: IRequestTransformer) => {
        if (!this.requestTransformers.includes(transformer)) {
            this.requestTransformers.push(transformer);
        }
    };

    public removeRequestTransformer = (transformer: IRequestTransformer) => {
        this.requestTransformers = this.requestTransformers.filter(
            requestTransformer => requestTransformer !== transformer
        );
    };

    public delete = <T>(payload: IPayload | string) => {
        if (typeof payload === 'string') {
            return this.makeRequest<T>(payload, 'delete');
        }

        return this.makeRequest<T>(
            payload.url,
            'delete',
            payload.body,
            payload.options,
            payload.headers
        );
    };

    public post = <T>(payload: IPayload) => {
        return this.makeRequest<T>(
            payload.url,
            'post',
            payload.body,
            payload.options,
            payload.headers,
            this.dataFetchHandler(payload)
        );
    };

    public patch = <T>(payload: IPayload) => {
        return this.makeRequest<T>(
            payload.url,
            'patch',
            payload.body,
            payload.options,
            payload.headers
        );
    };

    public put = <T>(payload: IPayload) => {
        return this.makeRequest<T>(
            payload.url,
            'put',
            payload.body,
            payload.options,
            payload.headers
        );
    };

    public get = <T>(
        payload: IGetPayload | string,
        { debounceInterval = 200, bypassCache = false }: IRequestOptions = {}
    ) => {
        let url;
        let options;
        let headers;
        let params;
        if (typeof payload === 'string') {
            url = payload;
        } else {
            url = payload.url;
            options = payload.options;
            headers = payload.headers;
            params = payload.params;
        }
        const urlReq = this.getRequestPayloads[url];
        const currentTime = new Date().getTime();
        if (urlReq && currentTime - urlReq.timestamp < debounceInterval) {
            return urlReq.promise;
        }

        delete this.getRequestPayloads[url];

        if (bypassCache) {
            headers = headers || {};
            headers[BYPASS_CACHE_HEADER_KEY] = BYPASS_CACHE_HEADER_VALUE;
        }

        const promise = this.makeRequest<T>(
            buildUrl(url, params),
            'get',
            undefined,
            options,
            headers
        );
        this.getRequestPayloads[url] = {
            promise,
            timestamp: new Date().getTime()
        };

        return this.getRequestPayloads[url].promise;
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    protected getRequestHeaders = async (headers?: any) => {
        // remove this logic when auth endpoint is final
        const CONTENT_TYPE_KEY = 'content-type';
        let contentTypeValue = 'application/json';
        if (headers && headers[CONTENT_TYPE_KEY]) {
            contentTypeValue = headers[CONTENT_TYPE_KEY];
        }

        const globalHeadersFromSettersArray: HeaderObject[] = [];

        await Promise.all(
            this.globalHeaderSetters.map(async globalHeaderSetter => {
                const setterHeaders = await globalHeaderSetter();
                globalHeadersFromSettersArray.push(setterHeaders);
            })
        );
        const globalHeadersFromSetters = globalHeadersFromSettersArray.reduce<HeaderObject>(
            (acc, headersItem) => ({ ...acc, ...headersItem }),
            {}
        );

        const httpHeader = new Headers({
            [CONTENT_TYPE_KEY]: contentTypeValue,
            ...this.globalHeaders,
            ...globalHeadersFromSetters,
            ...headers
        });

        return { headers: httpHeader };
    };

    protected handleError = (error: IError) => {
        console.error(error);
        throw error;
    };

    public makeRequest = async <T>(
        url: string,
        method: string,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        body?: any,
        options?: {},
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        headers?: any,
        dataFetcher = this.dataFetchHandler()
    ): Promise<IResponse<T>> => {
        let formattedBody = body;
        const requestHeaders = await this.getRequestHeaders(headers);
        // remove this logic when auth endpoint is final
        if (requestHeaders.headers.get('content-type') === 'application/json' && body) {
            formattedBody = JSON.stringify(body);
        }
        const config: IRequestTransformerConfig = {
            url: this.getUrl(url),
            method: method.toUpperCase(),
            mode: 'cors',
            ...options,
            ...requestHeaders,
            body: formattedBody
        };
        for (const transformer of this.requestTransformers) {
            await transformer(config);
        }
        const { url: reqUrl, ...init } = config;
        const fetchResponse = await fetch(reqUrl, init);

        const response: IResponse<T> = {
            data: null,
            reponseHeaders: fetchResponse.headers
        };

        try {
            if (this.isBodyResponseEmpty(fetchResponse)) {
                return response;
            }

            // handle unauthorized request (exclude login calls)
            // notify sign in service to get a new token
            // wait the token update
            // make again the call that failed
            if (
                this.isAuthFailureHandlingEnabled &&
                (fetchResponse.status === 401 || fetchResponse.status === 403) &&
                !/oauth/.test(url)
            ) {
                // clear auth token data from transporter
                this.removeGlobalHeader('AuthorizationToken');
                if (root.GlobalServiceBus) {
                    root.GlobalServiceBus.emit('app.elc-unauthorized-request');
                }
                await waitUntil(() => this.globalHeaders.hasOwnProperty('AuthorizationToken'));

                return this.makeRequest<T>(url, method, body, options, headers, dataFetcher);
            }
            response.data = await dataFetcher(fetchResponse);

            if (fetchResponse.status >= 300) {
                throw new ApiError(fetchResponse.status, response);
            }

            return response;
        } catch (error) {
            this.handleError(error);

            return response;
        }
    };
    private getUrl = (url: string) => {
        if (!this.baseUrl || url.includes(this.baseUrl)) {
            return url;
        }
        if (url.indexOf('http') === 0) {
            return url;
        }

        return this.baseUrl + url;
    };

    private isBodyResponseEmpty = (response: Response) => {
        if (response.status === 204) {
            return true;
        }

        if (
            response.headers.has('content-length') &&
            Number(response.headers.get('content-length')) === 0
        ) {
            return true;
        }
    };

    public jsonp = async <T>(url: string): Promise<IResponse<T>> => {
        const response = await fetchJsonp(url, {
            jsonpCallback: 'jsonp',
            jsonpCallbackFunction: 'jsonp'
        });

        return response.json();
    };

    private dataFetchHandler = (payload?: IPayload) =>
        payload && payload.returnAsPlainText
            ? (response: Response) => response.text()
            : (response: Response) => response.json();
}
